// geoFenceService.js
// Loads the organisation's visible geo-fences and normalizes them into Leaflet
// polygon rings ([lat,lng][]) so they can be overlaid on the maps as points of
// interest.
//
//   GET /geo-fence/visible?organisation=<orgId>

import { useState, useEffect } from 'react';
import { apiGet } from './apiService';
import { getOrganisationId } from './authService';

const extractList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.features)) return payload.features; // GeoJSON FeatureCollection
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.geoFences)) return payload.geoFences;
  if (Array.isArray(payload.geofences)) return payload.geofences;
  if (payload.data && Array.isArray(payload.data.features)) return payload.data.features;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  return [];
};

const nameOf = (g) =>
  (g && (g.name
    || (g.feature && g.feature.properties && (g.feature.properties.name || g.feature.properties.stand_id))
    || (g.properties && (g.properties.name || g.properties.title || g.properties.label))
    || g.title || g.label || g.code)) || 'Geo-fence';

// Convert a single coordinate to [lat, lng]. Handles {lat,lng} objects and
// [a,b] pairs. GeoJSON pairs are [lng,lat]; for our India data lng (~77) > lat
// (~28), so when the first value's magnitude is larger we treat it as [lng,lat].
const toLatLng = (c) => {
  if (!c) return null;
  if (typeof c === 'object' && !Array.isArray(c)) {
    const lat = Number(c.lat != null ? c.lat : c.latitude);
    const lng = Number(c.lng != null ? c.lng : (c.lon != null ? c.lon : c.longitude));
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    return null;
  }
  if (Array.isArray(c) && c.length >= 2) {
    const a = Number(c[0]);
    const b = Number(c[1]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    // [lng,lat] (GeoJSON) when |a| > 90, or when first looks like an Indian lng.
    if (Math.abs(a) > 90 || (Math.abs(a) > Math.abs(b) && Math.abs(b) <= 90)) return [b, a];
    return [a, b];
  }
  return null;
};

const ringFrom = (arr) => (Array.isArray(arr) ? arr.map(toLatLng).filter(Boolean) : []);

// Pull polygon ring(s) out of a geo-fence record across the shapes the API
// might use, returning an array of rings (each a [lat,lng][]).
const ringsOf = (g) => {
  if (!g) return [];
  // AssetPro wraps the GeoJSON under `feature.geometry`.
  const geom = (g.feature && g.feature.geometry) || g.geometry || g.geoJson || g.geojson || g;
  const type = geom && geom.type;
  const coords = geom && geom.coordinates;

  if (type === 'Polygon' && Array.isArray(coords)) {
    return coords.map(ringFrom).filter((r) => r.length >= 3);
  }
  if (type === 'MultiPolygon' && Array.isArray(coords)) {
    const out = [];
    coords.forEach((poly) => (poly || []).forEach((ring) => {
      const r = ringFrom(ring);
      if (r.length >= 3) out.push(r);
    }));
    return out;
  }
  // Custom shapes: coordinates / points / path as a flat ring.
  const flat = g.coordinates || g.points || g.path || g.polygon || g.vertices;
  if (Array.isArray(flat)) {
    // Could be a single ring [[..],[..]] or nested [[[..]]].
    if (flat.length && Array.isArray(flat[0]) && Array.isArray(flat[0][0])) {
      return flat.map(ringFrom).filter((r) => r.length >= 3);
    }
    const r = ringFrom(flat);
    return r.length >= 3 ? [r] : [];
  }
  return [];
};

export function normalizeGeoFences(payload) {
  return extractList(payload)
    .map((g, i) => {
      const rings = ringsOf(g);
      if (!rings.length) return null;
      return { id: g._id || g.id || `gf-${i}`, name: nameOf(g), rings, raw: g };
    })
    .filter(Boolean);
}

export async function loadGeoFences() {
  const orgId = getOrganisationId();
  if (!orgId) return { success: false, geoFences: [], error: 'No organisation id available' };
  const res = await apiGet(`/geo-fence/visible?organisation=${encodeURIComponent(orgId)}`);
  if (res && res.success === false) {
    return { success: false, geoFences: [], error: res.error };
  }
  const payload = res.data === undefined ? res : res.data;
  return { success: true, geoFences: normalizeGeoFences(payload), raw: payload };
}

// Hook: load the org geo-fences once. Returns { geoFences, loading, error }.
export function useGeoFences() {
  const [geoFences, setGeoFences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadGeoFences()
      .then((r) => {
        if (cancelled) return;
        if (r.success) setGeoFences(r.geoFences);
        else setError(r.error || 'Failed to load geo-fences');
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { geoFences, loading, error };
}
